import {FormBaseComponent, SteuernummerHelper, ValidatorService} from '@utils-lib';
import {Component, Input, OnInit} from '@angular/core';
import {Steuernummer} from '@nachdigal';
import {FormBuilder} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {DatevMessageService} from '@datev/ngx-ui-components';
import {isEmpty} from 'lodash';

@Component({
    selector: 'common-ui-steuernummer',
    templateUrl: './steuernummer.component.html'
})
export class SteuernummerComponent extends FormBaseComponent<Steuernummer> implements OnInit {

    @Input()
    public required = false;
    @Input()
    public steuernummerLabel = 'COMMON_UI.STEUERNUMMER.STEUERNUMMER';

    public steuernummerLabelTranslation = '';

    @Input()
    public narrowInputFields = false;
    public steuerMask = '';
    public prefix = '';
    public isInvalid = false;
    public isFinanzamtnummerMissing = false;
    private readonly steuernummerPattern = '^\\d{10,11}$';
    private readonly steuernummerRegexp = new RegExp(this.steuernummerPattern);

    constructor(private readonly formBuilder: FormBuilder,
                private readonly validatorService: ValidatorService,
                private readonly translateService: TranslateService,
                protected readonly messageService: DatevMessageService) {
        super(messageService);
    }

    public ngOnInit(): void {
        this.formatSteuernummer();
        super.ngOnInit();
        this.changeWarningVisibility();
        this.steuernummerLabelTranslation = this.translateService.instant(this.steuernummerLabel);
    }

    public formatSteuernummer(): void {
        if (this.data?.finanzamtnummer === null && this.data?.steuernummer === null) {
            return;
        }

        const format = SteuernummerHelper.getMaskFormat(this.data?.finanzamtnummer, this.data?.steuernummer);
        this.steuerMask = format.mask;
        if (format.prefix) {
            this.prefix = '\u200C' + format.prefix;
        } else {
            this.prefix = '';
        }
    }

    public changeWarningVisibility(): void {
        this.isFinanzamtnummerMissing = isEmpty(this.data.finanzamtnummer) && !isEmpty(this.data.steuernummer);
        this.isInvalid = !this.checkFinanzamtnummerIsValid()
            || !this.checkSteuernummerVsFinanzamtnummerIsValid()
            || !this.checkSteuernummerRegexIsValid();
    }

    protected createForm(): void {
        this.form = this.formBuilder.group({
            finanzamtnummer: [this.data.finanzamtnummer, [
                this.validatorService.conditionTrue(
                    this.checkSteuernummerVsFinanzamtnummerIsValid.bind(this),
                    this.translateService.instant('COMMON_UI.VALIDATION.FINANZAMTNUMMER_VS_STEUERNUMMER_INVALID')),
                this.validatorService.conditionTrue(
                    this.checkFinanzamtnummerIsValid.bind(this),
                    this.translateService.instant('COMMON_UI.VALIDATION.FINANZAMTNUMMER_INVALID_PATTERN')),
                this.validatorService.conditionalValidator(() => this.required, this.validatorService.required())
            ]],
            steuernummer: [this.formatSteuernummerForHessen(this.data.steuernummer, this.data.finanzamtnummer), [
                this.validatorService.conditionTrue(
                    () => !this.isFinanzamtnummerMissing,
                    this.translateService.instant('COMMON_UI.VALIDATION.FINANZAMTNUMMER_MISSING')),
                this.validatorService.pattern(
                    this.steuernummerPattern,
                    this.translateService.instant('COMMON_UI.VALIDATION.STEUERNUMMER_INVALID_PATTERN')),
                this.validatorService.conditionTrue(
                    this.checkSteuernummerVsFinanzamtnummerIsValid.bind(this),
                    this.translateService.instant('COMMON_UI.VALIDATION.STEUERNUMMER_VS_FINANZAMTNUMMER_INVALID')),
                this.validatorService.conditionalValidator(() => this.required, this.validatorService.required())
            ]]
        });
    }

    private checkFinanzamtnummerIsValid(): boolean {
        const finanzamtnummer = this.data?.finanzamtnummer;
        return !finanzamtnummer
            || (finanzamtnummer.length === 4 && /^(11|21|22|23|27|9|5|31|41|32|10|30|40|28|24|26).*$/.test(finanzamtnummer));
    }

    private checkSteuernummerVsFinanzamtnummerIsValid(): boolean {

        const finanzamtnummer = this.form?.get('finanzamtnummer').value;
        const steuernummer = this.form?.get('steuernummer').value as string;

        if (!finanzamtnummer || !steuernummer) {
            return true;
        }

        if (!this.steuernummerRegexp.test(steuernummer)) {
            return true;
        }

        // original steuernummer value is in different forms with slashes and spaces, we need to unify that
        // and get only the part that belongs to finanzamt (all except last 8 digits)
        if(finanzamtnummer.startsWith('26')){
          const finanzamtFromSteuernummer = steuernummer.replace(/[\/\s\-]/g, '').slice(0, -8);
          const finanzamtFromSteuernummerSuffix = finanzamtFromSteuernummer.slice(-2);
          return finanzamtFromSteuernummer && (finanzamtnummer.slice(-2) === finanzamtFromSteuernummerSuffix);
        }else {
          const finanzamtFromSteuernummer = steuernummer.replace(/[\/\s\-]/g, '').slice(0, -8);
          return finanzamtFromSteuernummer && finanzamtnummer.endsWith(finanzamtFromSteuernummer);
        }
    }

    private checkSteuernummerRegexIsValid(): boolean {
        const steuernummer = this.form?.get('steuernummer').value;
        return !steuernummer || this.steuernummerRegexp.test(steuernummer);
    }
    // For the "Hessen" case that has optional null (0) in the tax number!
    private formatSteuernummerForHessen(steuerNummer: string, finanzamtnummer: string): string{
      if(finanzamtnummer && steuerNummer){
        const isHessen = finanzamtnummer.startsWith('26');
        if(isHessen){
          if(steuerNummer.length === 10){
            steuerNummer = '0' + steuerNummer;
          }
        }
      }
      return steuerNummer;
    }
}
